import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, ArrowLeft, Send, MessageSquare } from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

function ReportInjury() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    injury_type: '',
    severity: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const severityOptions = [
    { value: 'Leve', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', desc: 'Puedo seguir entrenando' },
    { value: 'Moderada', color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', desc: 'Necesito descanso' },
    { value: 'Grave', color: 'bg-red-500/20 text-red-400 border-red-500/30', desc: 'Requiere atención médica' },
    { value: 'Crítica', color: 'bg-red-700/20 text-red-300 border-red-700/30', desc: 'Urgente - Emergencia' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.injury_type || !formData.severity || !formData.description) {
      toast.error('Completa todos los campos');
      return;
    }

    setSubmitting(true);
    
    try {
      await api.post('/injury-reports', formData);
      toast.success('Reporte enviado. Tu agencia ha sido notificada.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting injury report:', error);
      toast.error('Error al enviar reporte');
    } finally {
      setSubmitting(false);
    }
  };

  if (user?.role !== 'player') {
    return (
      <Layout>
        <div className="px-4 py-8 max-w-lg mx-auto">
          <div className="card-dark p-8 text-center">
            <AlertCircle className="w-12 h-12 text-zinc-400 mx-auto mb-4" />
            <p className="text-zinc-400">Solo disponible para jugadores</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#050505]" data-testid="report-injury-page">
        {/* Header */}
        <div className="sticky top-14 lg:top-0 z-30 bg-[#050505]/95 backdrop-blur-xl border-b border-white/10 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="p-2 -ml-2 text-zinc-400 active:scale-95">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Oswald' }}>REPORTAR LESIÓN</h1>
                <p className="text-xs text-zinc-500">Notificación inmediata</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 py-6 max-w-lg mx-auto pb-32 space-y-6">
          
          {/* Severity Selection - Big touch targets */}
          <div>
            <label className="text-sm text-zinc-400 mb-3 block">Severidad</label>
            <div className="grid grid-cols-2 gap-3">
              {severityOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, severity: option.value })}
                  className={`p-4 rounded-lg border text-left transition-all active:scale-95 ${
                    formData.severity === option.value 
                      ? option.color + ' border-2' 
                      : 'bg-zinc-900 border-zinc-800 text-zinc-400'
                  }`}
                  data-testid={`severity-${option.value.toLowerCase()}`}
                >
                  <p className="font-medium text-sm">{option.value}</p>
                  <p className="text-xs opacity-70 mt-1">{option.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Injury Type */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Tipo de lesión</label>
            <Input
              value={formData.injury_type}
              onChange={(e) => setFormData({ ...formData, injury_type: e.target.value })}
              placeholder="Ej: Esguince de tobillo, Desgarro muscular..."
              className="h-14 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-400"
              data-testid="injury-type-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm text-zinc-400 mb-2 block">Descripción</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="¿Cuándo ocurrió? ¿Cómo te sientes? ¿Qué tratamiento has recibido?"
              rows={5}
              className="bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-400 text-base resize-none"
              data-testid="injury-description-textarea"
            />
          </div>

          {/* WhatsApp Notice */}
          <div className="card-dark p-4 flex items-start gap-3 border-l-4 border-green-500">
            <MessageSquare className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zinc-300">
              Tu agencia recibirá una <strong className="text-green-400">notificación WhatsApp</strong> inmediata con los detalles de tu lesión.
            </p>
          </div>
        </div>

        {/* Fixed Bottom Action */}
        <div className="fixed bottom-16 lg:bottom-0 left-0 right-0 p-4 bg-[#050505]/95 backdrop-blur-xl border-t border-white/10 safe-area-bottom">
          <div className="max-w-lg mx-auto flex gap-3">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="h-14 px-6 border-zinc-700 text-white hover:bg-zinc-800"
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.severity}
              className="flex-1 h-14 bg-red-500 hover:bg-red-600 text-white font-bold text-base uppercase tracking-wide active:scale-95 transition-transform"
              data-testid="submit-injury-report-button"
            >
              {submitting ? 'Enviando...' : 'Enviar Reporte'}
              <Send className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default ReportInjury;
